import { SLIDE_PROMPT_CSS_VARIABLES, SLIDE_PROMPT_STYLESHEET } from './slide-prompt-stylesheet.ts'
import { PromptPosition, type HtmlPrompt, type PromptTheme, type PromptThemeButton, type PromptThemeText } from '../types/prompts.ts'
import { Env } from '../env.ts'

/**
 * `id` of the single `<style>` element carrying {@link SLIDE_PROMPT_STYLESHEET}. Every prompt
 * shares one stylesheet; this id is how {@link ensureStylesheetInjected} recognizes it is already
 * there instead of appending a duplicate per prompt.
 *
 * Deliberately outside the `push-sdk-prompt-<id>` namespace used for prompt shells. `prompt.id`
 * reaches that template unvalidated, so sharing the prefix would let a payload name a prompt such
 * that its shell and this stylesheet resolve to the same element.
 */
const STYLESHEET_ELEMENT_ID = 'pushly-slide-prompt-stylesheet'

/** Recognised `SlidePromptLayout` values. Anything else earns no `l-*` class at all. */
const RECOGNISED_LAYOUTS = new Set(['normal', 'stacked'])

/**
 * Recognised `position` axis values. `types/prompts.ts` documents `position` as being "matched
 * against a fixed set of recognised strings before they ever become a class name" — this is that
 * set. Anything else (including a value that merely happens to be a non-empty string, such as
 * `"top bottom"`) earns no `pos-d-*`/`pos-m-*` class for that axis, since a class name built
 * directly from an unvalidated string would let a payload smuggle extra class tokens onto the
 * container via an embedded space.
 */
const RECOGNISED_POSITIONS = new Set<string>(Object.values(PromptPosition))

/**
 * Normalizes a position axis before the allowlist lookup. Trimmed as well as lowercased, matching
 * the server renderer and the published contract - lowercasing alone made `" top "` stamp a class
 * on the server and nothing here, which puts the prompt at opposite edges of the viewport.
 */
const normalizeAxis = (value: string): string => value.trim().toLowerCase()

/** The whitespace CSS itself recognises. `\s` is wider and would admit values browsers reject. */
const WS = String.raw`[ \t\n\r\f]`

/** Signed integer or decimal, with or without a leading digit. */
const NUMBER = String.raw`[+-]?(?:\d+(?:\.\d+)?|\.\d+)`
const PERCENT = String.raw`${NUMBER}%`
/** An `<alpha-value>` is a bare number or a percentage. */
const ALPHA = String.raw`(?:${NUMBER}|${PERCENT})`
/** An `<hue>` is a number with an optional angle unit. */
const HUE = String.raw`${NUMBER}(?:deg|grad|rad|turn)?`
/** An rgb() channel is a number or a percentage; browsers tolerate the two being mixed. */
const CHANNEL = String.raw`(?:${NUMBER}|${PERCENT})`

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

/**
 * Both the legacy comma form and the modern space-separated form of rgb()/rgba() and hsl()/hsla().
 *
 * The grammar accepted here has to be wide enough for values that already exist in stored themes.
 * A narrower allowlist does not fail safe: a rejected colour falls back to a default, and a theme
 * that mixes an accepted colour with a rejected one used to render half-themed. Every alternative
 * is anchored and uses no nested quantifier over a shared class, so matching stays linear.
 */
const FUNCTIONAL_COLOR_PATTERNS = [
    new RegExp(String.raw`^rgba?\(${WS}*${CHANNEL}${WS}*,${WS}*${CHANNEL}${WS}*,${WS}*${CHANNEL}${WS}*(?:,${WS}*${ALPHA}${WS}*)?\)$`, 'i'),
    new RegExp(String.raw`^rgba?\(${WS}*${CHANNEL}${WS}+${CHANNEL}${WS}+${CHANNEL}${WS}*(?:\/${WS}*${ALPHA}${WS}*)?\)$`, 'i'),
    new RegExp(String.raw`^hsla?\(${WS}*${HUE}${WS}*,${WS}*${PERCENT}${WS}*,${WS}*${PERCENT}${WS}*(?:,${WS}*${ALPHA}${WS}*)?\)$`, 'i'),
    new RegExp(String.raw`^hsla?\(${WS}*${HUE}${WS}+${PERCENT}${WS}+${PERCENT}${WS}*(?:\/${WS}*${ALPHA}${WS}*)?\)$`, 'i'),
]

/**
 * The full CSS named-colour set, plus the two keywords valid wherever a colour is. This is
 * complete rather than a curated subset: a name the publisher legitimately saved and this list
 * omits is a colour the prompt silently renders wrong.
 */
const NAMED_COLORS = new Set([
    'aliceblue', 'antiquewhite', 'aqua', 'aquamarine', 'azure', 'beige', 'bisque', 'black',
    'blanchedalmond', 'blue', 'blueviolet', 'brown', 'burlywood', 'cadetblue', 'chartreuse',
    'chocolate', 'coral', 'cornflowerblue', 'cornsilk', 'crimson', 'cyan', 'darkblue', 'darkcyan',
    'darkgoldenrod', 'darkgray', 'darkgreen', 'darkgrey', 'darkkhaki', 'darkmagenta',
    'darkolivegreen', 'darkorange', 'darkorchid', 'darkred', 'darksalmon', 'darkseagreen',
    'darkslateblue', 'darkslategray', 'darkslategrey', 'darkturquoise', 'darkviolet', 'deeppink',
    'deepskyblue', 'dimgray', 'dimgrey', 'dodgerblue', 'firebrick', 'floralwhite', 'forestgreen',
    'fuchsia', 'gainsboro', 'ghostwhite', 'gold', 'goldenrod', 'gray', 'green', 'greenyellow',
    'grey', 'honeydew', 'hotpink', 'indianred', 'indigo', 'ivory', 'khaki', 'lavender',
    'lavenderblush', 'lawngreen', 'lemonchiffon', 'lightblue', 'lightcoral', 'lightcyan',
    'lightgoldenrodyellow', 'lightgray', 'lightgreen', 'lightgrey', 'lightpink', 'lightsalmon',
    'lightseagreen', 'lightskyblue', 'lightslategray', 'lightslategrey', 'lightsteelblue',
    'lightyellow', 'lime', 'limegreen', 'linen', 'magenta', 'maroon', 'mediumaquamarine',
    'mediumblue', 'mediumorchid', 'mediumpurple', 'mediumseagreen', 'mediumslateblue',
    'mediumspringgreen', 'mediumturquoise', 'mediumvioletred', 'midnightblue', 'mintcream',
    'mistyrose', 'moccasin', 'navajowhite', 'navy', 'oldlace', 'olive', 'olivedrab', 'orange',
    'orangered', 'orchid', 'palegoldenrod', 'palegreen', 'paleturquoise', 'palevioletred',
    'papayawhip', 'peachpuff', 'peru', 'pink', 'plum', 'powderblue', 'purple', 'rebeccapurple',
    'red', 'rosybrown', 'royalblue', 'saddlebrown', 'salmon', 'sandybrown', 'seagreen', 'seashell',
    'sienna', 'silver', 'skyblue', 'slateblue', 'slategray', 'slategrey', 'snow', 'springgreen',
    'steelblue', 'tan', 'teal', 'thistle', 'tomato', 'turquoise', 'violet', 'wheat', 'white',
    'whitesmoke', 'yellow', 'yellowgreen',
    'transparent', 'currentcolor',
])

/**
 * Validates a theme-supplied colour before it is ever written into a CSS custom property. Accepts
 * hex, `rgb()`/`rgba()`, and a fixed named-colour allowlist only — CSS injection through a custom
 * property is not inert (a `url()` value can be used to exfiltrate data, and unbalanced content
 * can smuggle additional declarations into a stylesheet that later reads the property), so
 * anything not matching one of these three shapes is rejected outright rather than sanitized.
 */
export function isSafeCssColor(value: unknown): value is string {
    if (typeof value !== 'string') {
        return false
    }

    const trimmed = value.trim()
    if (trimmed.length === 0) {
        return false
    }

    if (HEX_COLOR_PATTERN.test(trimmed) || NAMED_COLORS.has(trimmed.toLowerCase())) {
        return true
    }

    return FUNCTIONAL_COLOR_PATTERNS.some((pattern) => pattern.test(trimmed))
}

/**
 * Validates a theme-supplied image URL before it is written into a CSS custom property.
 *
 * Only https is accepted — `javascript:` would execute in the publisher's origin if anything
 * ever evaluated it, and `data:` can embed arbitrary content (including markup) directly in the
 * stylesheet. `http:` is refused too: a secure page blocks the mixed-content load anyway, and on
 * an insecure page it would let a network attacker substitute the image.
 *
 * The scheme check is not sufficient on its own, which is why the serialized form is checked too.
 * See the comment on the character test below.
 */
export function isSafeImageUrl(value: unknown): value is string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        return false
    }

    let parsed: URL
    try {
        parsed = new URL(value)
    } catch {
        return false
    }

    if (parsed.protocol !== 'https:') {
        return false
    }

    // `URL` percent-encodes these in the path, query, fragment and userinfo, but NOT in the host,
    // so a well-formed URL can still carry a raw quote and close the `url("...")` token early -
    // turning one declaration into two and issuing a request the scheme check was there to stop.
    return !/["'()\\\s]/.test(parsed.href)
}

/**
 * Structural shape check only — does NOT require non-empty `text`. An empty title, subtitle, or
 * button label is a legitimate, deliberate publisher configuration (a title-only prompt, say) and
 * must still render as an empty node, matching the server renderer. Whether a *theme as a whole*
 * has anything worth rendering is a separate question, answered by {@link themeHasUsableContent}.
 */
function isThemeTextConfig(value: unknown): value is PromptThemeText {
    return typeof value === 'object' && value !== null && typeof (value as PromptThemeText).text === 'string'
}

/** The text for a themed row/button, or `''` when the field is absent or malformed. */
function themeText(value: PromptThemeText | undefined): string {
    return isThemeTextConfig(value) ? value.text : ''
}

/**
 * A theme carries "nothing usable at all" when none of its fields hold anything a publisher could
 * have configured: no text-config object for title/subtitle/either button, no valid colour, no
 * valid image, no recognised layout. That is the stale-payload case — a prompt published before
 * theme fields existed carries only `position` — and it is the *only* thing that should make
 * {@link buildSlidePromptElement} return `null`.
 *
 * A theme with any field set — even just a background colour, even just an empty-string subtitle
 * — has something a publisher deliberately configured and must render, blank copy and all.
 * Requiring non-empty text here (as an earlier version of this function did) suppressed the
 * *entire* prompt over one blank field, which is exactly the silent-failure mode this renderer
 * exists to avoid: a themed, publisher-configured prompt would vanish and look identical to no
 * traffic at all.
 */
function themeHasUsableContent(theme: PromptTheme): boolean {
    return (
        isThemeTextConfig(theme.title) ||
        isThemeTextConfig(theme.subtitle) ||
        isThemeTextConfig(theme.allow_button) ||
        isThemeTextConfig(theme.dismiss_button) ||
        isSafeCssColor(theme.background_color) ||
        isSafeImageUrl(theme.image) ||
        (typeof theme.layout === 'string' && RECOGNISED_LAYOUTS.has(theme.layout))
    )
}

/**
 * The theme's colours, resolved together.
 *
 * Resolution is all-or-nothing on purpose. Applying each property independently lets a theme that
 * mixes syntaxes land half-applied — a rejected `background_color` leaves the container on its
 * white default while an accepted `title.text_color` of near-white still applies, producing a
 * prompt whose copy is invisible. Falling back to the complete default palette is always legible,
 * and matches what a theme carrying no colours at all already renders.
 */
function resolveThemeColors(
    theme: PromptTheme,
    title: PromptThemeText | undefined,
    subtitle: PromptThemeText | undefined,
    allowButton: PromptThemeButton | undefined,
    dismissButton: PromptThemeButton | undefined,
): Array<[string, string]> {
    const candidates: Array<[string, unknown]> = [
        [SLIDE_PROMPT_CSS_VARIABLES.containerBackground, theme.background_color],
        [SLIDE_PROMPT_CSS_VARIABLES.titleColor, title?.text_color],
        [SLIDE_PROMPT_CSS_VARIABLES.subtitleColor, subtitle?.text_color],
        [SLIDE_PROMPT_CSS_VARIABLES.allowButtonBackground, allowButton?.background_color],
        [SLIDE_PROMPT_CSS_VARIABLES.allowButtonColor, allowButton?.text_color],
        [SLIDE_PROMPT_CSS_VARIABLES.dismissButtonBackground, dismissButton?.background_color],
        [SLIDE_PROMPT_CSS_VARIABLES.dismissButtonColor, dismissButton?.text_color],
    ]

    // An absent colour is not a rejected one: the publisher simply did not set it, and the
    // stylesheet's own `var(name, <default>)` covers it. Only a value that is present and
    // unusable forces the whole palette back to its defaults.
    const present = candidates.filter(([, value]) => value !== undefined && value !== null && value !== '')
    if (present.some(([, value]) => !isSafeCssColor(value))) {
        return []
    }

    return present.map(([name, value]) => {
        const trimmed = (value as string).trim()

        // Named colours are emitted lowercased, because the set lookup folds case and
        // `String.toLowerCase` maps a few non-ASCII codepoints onto ASCII letters (U+212A KELVIN
        // SIGN becomes `k`). Emitting the original text would set the custom property to a keyword
        // CSS does not recognise — and because the property IS set, the stylesheet's
        // `var(name, default)` fallback never applies, so the element resolves to an invalid value
        // rather than to the default. A container goes transparent; inherited copy takes the
        // publisher's own colour and can vanish.
        return [name, NAMED_COLORS.has(trimmed.toLowerCase()) ? trimmed.toLowerCase() : trimmed]
    })
}

/**
 * Builds the `s-slide`/`l-*`/`pos-*` modifier classes on `.push-sdk-prompt-container`, mirroring
 * `sea-vent-js/src/views/prompt/prompt-root.tsx:17-21` as three independent rules rather than one
 * passthrough:
 *
 * - `s-slide` is unconditional — this renderer only ever builds slide prompts.
 * - `l-normal`/`l-stacked` are added only for those two recognised `layout` values; any other
 *   value (including one omitted entirely) earns no `l-*` class.
 * - `pos-d-<desktop>`/`pos-m-<mobile>` are added only when `position` is present and the
 *   corresponding axis, lowercased, is one of {@link RECOGNISED_POSITIONS}. The source component
 *   computes both class names unconditionally off `position` alone (even reaching into a missing
 *   `desktop` or `mobile`), which would emit a literal `pos-d-undefined` token for a malformed
 *   payload — this renderer instead validates each axis independently against the fixed set
 *   before adding its class, since `position` arrives from the same attacker-reachable payload as
 *   everything else in the theme, and an unvalidated string could smuggle extra class tokens onto
 *   the container through an embedded space (e.g. `"top evil-class"`).
 */
function buildContainerClassName(theme: PromptTheme): string {
    const classes = ['push-sdk-prompt-container', 's-slide']

    if (typeof theme.layout === 'string' && RECOGNISED_LAYOUTS.has(theme.layout)) {
        classes.push(`l-${theme.layout}`)
    }

    const position = theme.position
    if (position && typeof position === 'object') {
        if (typeof position.desktop === 'string' && RECOGNISED_POSITIONS.has(normalizeAxis(position.desktop))) {
            classes.push(`pos-d-${normalizeAxis(position.desktop)}`)
        }
        if (typeof position.mobile === 'string' && RECOGNISED_POSITIONS.has(normalizeAxis(position.mobile))) {
            classes.push(`pos-m-${normalizeAxis(position.mobile)}`)
        }
    }

    return classes.join(' ')
}

function buildTextRow(document: Document, className: string, text: string): HTMLDivElement {
    const row = document.createElement('div')
    row.className = className

    const span = document.createElement('span')
    span.textContent = text
    row.append(span)

    return row
}

function buildButton(
    document: Document,
    modifier: 'allow' | 'dismiss',
    config: PromptThemeButton | undefined,
    onClick: () => void,
): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `pushly-prompt-btn pushly-prompt-btn-${modifier}`

    const span = document.createElement('span')
    span.textContent = themeText(config)
    button.append(span)

    button.addEventListener('click', () => onClick())

    return button
}

/**
 * Applies every theme-driven value as a validated CSS custom property on `element`, using the
 * names in {@link SLIDE_PROMPT_CSS_VARIABLES} so a typo cannot silently no-op a style. Custom
 * properties inherit down the DOM tree, so setting them on the outermost shell reaches every rule
 * in {@link SLIDE_PROMPT_STYLESHEET} that consumes them regardless of how deep the matching
 * element sits.
 *
 * The image is the one pair applied together rather than independently: `imageDisplay` (which
 * reveals `.pushly-prompt-image`) is set only when `image` also validates, so an invalid image
 * URL cannot reveal an empty box — the node stays present but hidden, per the structural
 * contract's "always present, CSS-only visibility" rule.
 */
function applyThemeCustomProperties(
    element: HTMLElement,
    theme: PromptTheme,
    title: PromptThemeText | undefined,
    subtitle: PromptThemeText | undefined,
    allowButton: PromptThemeButton | undefined,
    dismissButton: PromptThemeButton | undefined,
): void {
    for (const [propertyName, value] of resolveThemeColors(theme, title, subtitle, allowButton, dismissButton)) {
        element.style.setProperty(propertyName, value)
    }

    if (isSafeImageUrl(theme.image)) {
        // Safe to embed only because `isSafeImageUrl` rejected every character that could close
        // the `url("...")` token - scheme validation and `URL` normalization alone do not, since
        // a quote in the host survives normalization untouched.
        const normalizedImageUrl = new URL(theme.image).href
        element.style.setProperty(SLIDE_PROMPT_CSS_VARIABLES.image, `url("${normalizedImageUrl}")`)
        element.style.setProperty(SLIDE_PROMPT_CSS_VARIABLES.imageDisplay, 'block')
    }
}

/**
 * Injects {@link SLIDE_PROMPT_STYLESHEET} into `document.head` exactly once. Safe to call once per
 * prompt build — every call after the first is a no-op, found by id rather than by re-scanning
 * `<style>` contents.
 */
function ensureStylesheetInjected(document: Document): void {
    // Ownership is established by content rather than by id. Looking the element up by id hands
    // control of prompt styling to the host page - any element carrying that id would suppress the
    // stylesheet and leave the prompt unstyled - and removing what it finds would delete publisher
    // DOM, including their rules if the element is a <style> of their own. Scanning for our own
    // sheet avoids both: a host element keeping the id is simply ignored.
    const alreadyInjected = Array.from(document.getElementsByTagName('style')).some(
        (element) => element.textContent === SLIDE_PROMPT_STYLESHEET,
    )
    if (alreadyInjected) {
        return
    }

    const style = document.createElement('style')
    style.id = STYLESHEET_ELEMENT_ID
    style.textContent = SLIDE_PROMPT_STYLESHEET

    // Appended to <body>, not <head>. The server shipped this CSS inside the prompt markup, which
    // landed last in document order, so an equal-specificity publisher rule lost the tie. From
    // <head> it would win instead, letting any publisher <style>, CSS-in-JS insertion or tag
    // manager reposition or hide the prompt without needing !important.
    const host = document.body ?? document.head
    host.append(style)
}

/**
 * Builds the slide prompt DOM entirely from structured theme data — `createElement` +
 * `textContent` + `addEventListener` only, never through any HTML-parsing assignment or fragment
 * API — so a compromised CDN payload can supply markup-shaped strings (`<img onerror=...>` as a
 * title, say) without any of it ever being parsed as markup.
 *
 * Returns `null` when `prompt.theme` is absent or carries nothing usable at all (see
 * {@link themeHasUsableContent}) — the stale-payload / no-theme-yet case. Deliberately does **not**
 * fall back to `prompt.template_html` — a fallback would reintroduce the injection sink this
 * renderer exists to remove.
 */
export function buildSlidePromptElement(
    prompt: HtmlPrompt,
    handlers: { onAllow: () => void; onDismiss: () => void },
): HTMLElement | null {
    const theme = prompt.theme

    if (!theme || typeof theme !== 'object' || !themeHasUsableContent(theme)) {
        return null
    }

    const document = Env._document
    ensureStylesheetInjected(document)

    const shell = document.createElement('div')
    shell.className = 'push-sdk-prompt-shell'
    shell.id = `push-sdk-prompt-${prompt.id}`

    const container = document.createElement('div')
    container.className = buildContainerClassName(theme)

    const wrapper = document.createElement('div')
    wrapper.className = 'pushly-prompt-wrapper'

    // Always built, per the structural contract — visibility is CSS-only, driven by whether
    // `applyThemeCustomProperties` below ends up setting the image custom properties.
    const image = document.createElement('div')
    image.className = 'pushly-prompt-image'

    const content = document.createElement('div')
    content.className = 'pushly-prompt-content'

    const contentCopy = document.createElement('div')
    contentCopy.className = 'pushly-prompt-content-copy'
    contentCopy.append(
        buildTextRow(document, 'pushly-prompt-title', themeText(theme.title)),
        buildTextRow(document, 'pushly-prompt-subtitle', themeText(theme.subtitle)),
    )

    const buttons = document.createElement('div')
    buttons.className = 'pushly-prompt-btns'
    buttons.append(
        buildButton(document, 'allow', theme.allow_button, handlers.onAllow),
        buildButton(document, 'dismiss', theme.dismiss_button, handlers.onDismiss),
    )

    content.append(contentCopy, buttons)
    wrapper.append(image, content)
    container.append(wrapper)
    shell.append(container)

    applyThemeCustomProperties(shell, theme, theme.title, theme.subtitle, theme.allow_button, theme.dismiss_button)

    return shell
}
